import { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import {
  ActionButton,
  Background,
  CheckoutBox,
  CustomBackToGameButton,
  MessagePage,
} from '@appcharge/shared-ui';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, IconButton, InputAdornment, Typography } from '@mui/material';
import useTheme from 'api/useTheme';
import { EAssetType } from 'constants/enums';
import { useFormikContext } from 'formik';

import AcCard from 'components/AcCard/AcCard';
import AcInput from 'components/AcInput/AcInput';
import { Flex } from 'components/ui/flex';
import { Separator } from 'components/ui/separator';
import UploadImage from 'components/UploadImage/UploadImage';

import { ThemeLoader } from '../ThemeLoader';

import { CompletedThemeFontStyleFields } from './CompletedThemeFontStyleFields';
import { defaultCompletedTheme } from './defaults';
import { CompletedThemeFormValues } from './types';

import 'style/forms.scss';

const backToGameButtonHelperText =
  'The uploaded image will replace the default button image. Recommended dimensions: 46 × 240 px (W × H).';
const customCloseIconHelperText =
  'The uploaded image will replace the default close icon.';


export const CompletedTheme: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { values, handleBlur, handleChange, setFieldValue, errors, touched } =
    useFormikContext<CompletedThemeFormValues>();

  const { publisherId } = useParams();

  const defaultValues = useMemo(() => {
    const defaults = {
      purchaseCompletedModal: defaultCompletedTheme.purchaseCompletedModal,
      additionalText: defaultCompletedTheme.additionalText,
      productsQuantity: defaultCompletedTheme.productsQuantity,
      backgroundOpacity: defaultCompletedTheme.backgroundOpacity,
      closeIcon: defaultCompletedTheme.closeIcon,
      backToGameButton: defaultCompletedTheme.backToGameButton,
      deepLinks: defaultCompletedTheme.deepLinks
    };

    if (!values) {
      return defaults;
    }

    return {
      ...values,
      purchaseCompletedModal: {
        color:
          values.purchaseCompletedModal?.color ||
          defaultCompletedTheme.purchaseCompletedModal.color,
        size: values.purchaseCompletedModal?.size ??
          defaultCompletedTheme.purchaseCompletedModal.size
        ,
        text:
          values.purchaseCompletedModal?.text ||
          defaultCompletedTheme.purchaseCompletedModal.text
        ,
        weight: values.purchaseCompletedModal?.weight ||
          defaultCompletedTheme.purchaseCompletedModal.weight
      },
      additionalText: {
        color: values.additionalText?.color ||
          defaultCompletedTheme.additionalText.color
        ,
        size:
          values.additionalText?.size ??
          defaultCompletedTheme.additionalText.size
        ,
        text: values.additionalText?.text || defaultCompletedTheme.additionalText.text
        ,
        weight: values.additionalText?.weight ||
          defaultCompletedTheme.additionalText.weight
      },
      productsQuantity: {
        color:
          values.productsQuantity?.color ||
          defaultCompletedTheme.productsQuantity?.color,
        size:
          values.productsQuantity?.size ??
          defaultCompletedTheme.productsQuantity?.size,
        weight:
          values.productsQuantity?.weight ||
          defaultCompletedTheme.productsQuantity?.weight
      },
      backgroundOpacity:
        values.backgroundOpacity ?? defaultCompletedTheme.backgroundOpacity,
      closeIcon: values.closeIcon ?? defaultCompletedTheme.closeIcon,
      backToGameButton: {
        image:
          values.backToGameButton?.image ??
          defaultCompletedTheme.backToGameButton.image,
        text: values.backToGameButton?.text ||
          defaultCompletedTheme.backToGameButton.text

      },
      deepLinks: values.deepLinks || defaultCompletedTheme.deepLinks
    };
  }, [values]);

  const {
    getCompletedTheme: completeTheme,
    getGeneralTheme: generalTheme,
    getStoreScreen: screenTheme
  } = useTheme(publisherId || undefined);

  const hasValues = useMemo(() => Object.keys(values).length > 0, [values]);

  useEffect(
    () =>
      setIsLoading(
        completeTheme.isLoading || !completeTheme.data || !hasValues
      ),
    [completeTheme?.isLoading, completeTheme?.data, hasValues]
  );

  if (isLoading) {
    return <ThemeLoader />;
  }

  return (
    <Grid container className="formContent">
      <Grid item xs={5}>
        <Typography
          variant="h6"
          sx={{
            marginBottom: '12px',
            fontFamily: 'Inter, Montserrat, serif',
            fontWeight: '600'
          }}
        >
          Completed Page
        </Typography>
        <Flex className="gap-6">
          <AcCard stackContainer={false} title="General">
            <Grid container rowSpacing={2} columnSpacing={1.5}>
              <Grid item xs={12}>
                <UploadImage
                  title="Custom ‘X’ Icon"
                  buttonText="Upload"
                  variant="storeSettings"
                  className="w-full"
                  description={customCloseIconHelperText}
                  forceType={EAssetType.GENERAL}
                  selectedImageUrl={values.closeIcon}
                  data-testid="completedCloseIconUpload"
                  buttonDataTestId="uploadCompletedCloseIcon"
                  onUpload={(url: string) => setFieldValue('closeIcon', url)}
                />
              </Grid>
              <Grid item xs={12}>
                <AcInput
                  header="Background Opacity"
                  name="backgroundOpacity"
                  type="number"
                  dataTestId="completedBackgroundOpacity"
                  slotProps={{
                    htmlInput: {
                      max: 100,
                      min: 0
                    },
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      )
                    }
                  }}
                  value={values.backgroundOpacity ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.backgroundOpacity &&
                    Boolean(errors.backgroundOpacity)
                  }
                  helperText={
                    touched.backgroundOpacity
                      ? errors.backgroundOpacity?.toString()
                      : ''
                  }
                />
              </Grid>
            </Grid>
          </AcCard>
          <AcCard
            stackContainer={false}
            title="Purchase Completed Modal"
            description="Congratulate your players on a successful purchase and let them know what's next."
          >
            <Grid container rowSpacing={2} columnSpacing={1.5}>
              <Grid item xs={12}>
                <AcInput
                  header="Header Text"
                  name="purchaseCompletedModal.text"
                  value={defaultValues.purchaseCompletedModal.text}
                  tooltip="This will be the header of the success screen."
                  type="text"
                  dataTestId="completedHeaderText"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.purchaseCompletedModal?.text &&
                    Boolean(errors.purchaseCompletedModal?.text)
                  }
                  helperText={
                    touched.purchaseCompletedModal?.text
                      ? errors.purchaseCompletedModal?.text?.toString()
                      : ''
                  }
                />
              </Grid>
              <CompletedThemeFontStyleFields
                colorName="purchaseCompletedModal.color"
                sizeName="purchaseCompletedModal.size"
                weightName="purchaseCompletedModal.weight"
                colorDefaultValue={defaultValues.purchaseCompletedModal.color}
                sizeValue={defaultValues.purchaseCompletedModal.size}
                weightValue={defaultValues.purchaseCompletedModal.weight}
                sizeMin={26}
                sizeMax={40}
                testIdPrefix="completedHeader"
              />
            </Grid>
          </AcCard>
          <AcCard stackContainer={false} title="Additional Text">
            <Grid container rowSpacing={2} columnSpacing={1.5}>
              <Grid item xs={12}>
                <AcInput
                  header="Body Text"
                  name="additionalText.text"
                  value={defaultValues.additionalText.text}
                  tooltip=""
                  type="text"
                  dataTestId="completedText"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.additionalText?.text &&
                    Boolean(errors.additionalText?.text)
                  }
                  helperText={
                    touched.additionalText?.text
                      ? errors.additionalText?.text?.toString()
                      : ''
                  }
                />
              </Grid>
              <CompletedThemeFontStyleFields
                colorName="additionalText.color"
                sizeName="additionalText.size"
                weightName="additionalText.weight"
                colorDefaultValue={{
                  colorOne: defaultValues.additionalText.color
                }}
                sizeValue={defaultValues.additionalText.size ?? ''}
                weightValue={defaultValues.additionalText.weight}
                sizeMin={12}
                sizeMax={24}
                testIdPrefix="completedText"
                solidColorOnly
              />
            </Grid>
          </AcCard>
          <Separator className="my-1 bg-base-border" />
          <AcCard
            stackContainer={false}
            title="Products Quantity"
            description="Set the font and text style for the quantity numbers shown next to products on the Completed Page."
          >
            <Grid container rowSpacing={2} columnSpacing={1.5}>
              <CompletedThemeFontStyleFields
                colorName="productsQuantity.color"
                sizeName="productsQuantity.size"
                weightName="productsQuantity.weight"
                colorDefaultValue={{
                  colorOne: defaultValues.productsQuantity?.color || 'white'
                }}
                sizeValue={defaultValues.productsQuantity?.size ?? ''}
                weightValue={defaultValues.productsQuantity?.weight}
                sizeMin={10}
                sizeMax={32}
                testIdPrefix="completedProductsQuantity"
                solidColorOnly
              />
            </Grid>
          </AcCard>
        </Flex>
        <Separator className="my-6 bg-base-border" />
        <Flex className="gap-6">
          <AcCard stackContainer={false} title="Back to Game Button">
            <Grid container rowSpacing={2} columnSpacing={1.5}>
              <Grid item xs={12}>
                <AcInput
                  headerSize="14"
                  header="Button Text"
                  name="backToGameButton.text"
                  value={defaultValues.backToGameButton.text}
                  dataTestId="completedBackToGameButtonText"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.backToGameButton?.text &&
                    Boolean(errors.backToGameButton?.text)
                  }
                  helperText={
                    touched.backToGameButton?.text
                      ? errors.backToGameButton?.text?.toString()
                      : ''
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <UploadImage
                  title="Button Image"
                  buttonText="Upload"
                  variant="storeSettings"
                  className="w-full"
                  description={backToGameButtonHelperText}
                  forceType={EAssetType.BACK_TO_GAME_BUTTON_IMAGE}
                  selectedImageUrl={values.backToGameButton?.image || ''}
                  data-testid="completedBackToGameButtonImageUpload"
                  buttonDataTestId="uploadCompletedBackToGameButtonImage"
                  onUpload={(url: string) =>
                    setFieldValue('backToGameButton.image', url)
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <AcInput
                  headerSize="14"
                  header="Android"
                  name="deepLinks.android"
                  value={defaultValues.deepLinks.android}
                  dataTestId="completedDeepLinksAndroid"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.deepLinks?.android &&
                    Boolean(errors.deepLinks?.android)
                  }
                  helperText={
                    touched.deepLinks?.android
                      ? errors.deepLinks?.android?.toString()
                      : ''
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <AcInput
                  headerSize="14"
                  header="Apple (iOS)"
                  name="deepLinks.ios"
                  value={defaultValues.deepLinks.ios}
                  dataTestId="completedDeepLinksIos"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.deepLinks?.ios && Boolean(errors.deepLinks?.ios)
                  }
                  helperText={
                    touched.deepLinks?.ios
                      ? errors.deepLinks?.ios?.toString()
                      : ''
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <AcInput
                  headerSize="14"
                  header="Web"
                  name="deepLinks.web"
                  value={defaultValues.deepLinks.web}
                  dataTestId="completedDeepLinksWeb"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.deepLinks?.web && Boolean(errors.deepLinks?.web)
                  }
                  helperText={
                    touched.deepLinks?.web
                      ? errors.deepLinks?.web?.toString()
                      : ''
                  }
                />
              </Grid>
            </Grid>
          </AcCard>
        </Flex>
      </Grid>
      <Grid item xs={7} className="iphone-mock-wrapper">
        <div className="store-preview-wrapper iphone-mock">
          <Background
            backgroundImageDesktop={generalTheme?.data?.backgroundImageMobile}
            backgroundImageMobile={generalTheme?.data?.backgroundImageMobile}
            width="100%"
            height="100%"
            position="absolute"
          >
            {(defaultValues.backgroundOpacity ?? 0) > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: `rgba(0, 0, 0, ${(defaultValues.backgroundOpacity ?? 0) / 100})`,
                  pointerEvents: 'none',
                  zIndex: 1
                }}
              />
            )}
            <Box
              sx={{
                position: 'relative',
                zIndex: 2,
                width: '100%',
                height: '100%'
              }}
            >
              <MessagePage
                headerText={defaultValues.purchaseCompletedModal.text}
                headerColor={defaultValues.purchaseCompletedModal.color}
                headerSize={defaultValues.purchaseCompletedModal.size}
                headerWeight={defaultValues.purchaseCompletedModal.weight}
                text={[defaultValues.additionalText.text || '']}
                textColor={defaultValues.additionalText.color}
                textSize={defaultValues.additionalText.size}
                textWeight={defaultValues.additionalText.weight}
                fontFamily={generalTheme?.data?.font}
                preview={true}
              >
                <Box
                  sx={{
                    order: -1,
                    alignSelf: 'flex-end',
                    flexShrink: 0,
                    zIndex: 3
                  }}
                >
                  <IconButton
                    disabled
                    sx={{ p: 0.5, width: 32, height: 32, flexShrink: 0 }}
                  >
                    {defaultValues.closeIcon ? (
                      <Box
                        component="img"
                        src={defaultValues.closeIcon}
                        alt="Close"
                        sx={{
                          width: 16,
                          height: 16,
                          flexShrink: 0,
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <CloseIcon sx={{ fontSize: 16, color: 'white' }} />
                    )}
                  </IconButton>
                </Box>
                <CheckoutBox height="135px" width="90%">
                  <Typography
                    data-testid="storeSuccessProductAmount"
                    variant="body2"
                    component="span"
                    style={{
                      color: defaultValues.productsQuantity?.color || 'white',
                      fontSize: `${defaultValues.productsQuantity?.size}px`,
                      fontWeight: defaultValues.productsQuantity?.weight,
                      fontFamily: generalTheme?.data?.font || 'inherit'
                    }}
                  >
                    x10
                  </Typography>
                </CheckoutBox>
                {defaultValues.backToGameButton.image ? (
                  <CustomBackToGameButton
                    buttonImage={defaultValues.backToGameButton.image}
                    isMockPreview={true}
                    getOffers={() => undefined}
                  />
                ) : (
                  <ActionButton
                    action={() => null}
                    text={defaultValues.backToGameButton.text}
                    colors={screenTheme?.data?.buttonColor}
                    textColor={screenTheme?.data?.buttonTextColor}
                  />
                )}
              </MessagePage>
            </Box>
          </Background>
        </div>
      </Grid>
    </Grid>
  );
};
